import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { KnxDeviceState } from '../version.service';
import { DeepLinkService } from '../shared/deep-link.service';
import { updatedAgo as fmtAgo } from '../shared/format.utils';
import { knxLocationName } from '../shared/knx-names';
import {
  KnxFloorGroup, KnxRoomGroup, KnxSinceFormatter, MOBILE_VIEW_QUERY, PhoneMedia, groupByLocation, loadFlag, pct, saveFlag,
} from './knx-devices.utils';

export type KnxSwitchKind = 'light' | 'socket';
type SwitchState = 'on' | 'off' | 'unknown';

interface SwitchRow {
  id:         string;
  name:       string;
  floor:      string | null;
  room:       string | null;
  state:      SwitchState;
  /** i18n key of the pill text ("AN" / "AUS"); null for an unknown state (shown as "–"). */
  stateKey:   string | null;
  /** Pill classes: amber (lights) / green (sockets) when on, muted outline when off, dashed when unknown. */
  pillCls:    string;
  /** Dimmers that are on: brightness rounded to whole %; null otherwise. */
  brightness: number | null;
  sinceTs:    number | null;
  since:      string | null;   // "18:42" / "Mo. 18:42" / "12.10. 18:42"
  sinceFull:  string | null;   // tooltip with the full date
}

/** A room card; `on` / `total` always count the whole room, also while only switched-on rows are shown. */
interface SwitchRoom extends KnxRoomGroup<SwitchRow> { on: number; total: number; }
type SwitchFloor = KnxFloorGroup<SwitchRow, SwitchRoom>;

interface SwitchSummary {
  total:     number;
  on:        number;
  off:       number;
  rooms:     number;
  roomsOn:   number;
  dimmers:   number;
  dimmersOn: number;
  avgDimPct: number | null;   // Ø brightness of the dimmers that are on
  last:      SwitchRow | null; // most recent observed change
  lastWhere: string;           // its floor + room in the UI language ("E3 Living room")
}

/** Per kind: texts, the saved "Nur an" flag (localStorage), its deep-link key and the sub-tab's view path. */
const KINDS: Record<KnxSwitchKind, {
  title: string; icon: string; flagKey: string; linkKey: string; view: string; roomsOn: string; allOff: string; onCls: string;
}> = {
  light:  { title: 'KNX_TAB_LIGHTS',  icon: '💡', flagKey: 'knxLightsOnlyOn',  linkKey: 'knx.lights.onlyOn',  view: 'knx/lights',
            roomsOn: 'KNX_ROOMS_LIT',     allOff: 'KNX_LIGHTS_ALL_OFF',  onCls: 'text-bg-warning knx-glow' },
  socket: { title: 'KNX_TAB_SOCKETS', icon: '🔌', flagKey: 'knxSocketsOnlyOn', linkKey: 'knx.sockets.onlyOn', view: 'knx/sockets',
            roomsOn: 'KNX_ROOMS_POWERED', allOff: 'KNX_SOCKETS_ALL_OFF', onCls: 'text-bg-success' },
};

/** Read-only KNX lights or switchable sockets: summary tiles and floor → room cards with AN / AUS pills. */
@Component({
  selector: 'app-knx-switches',
  templateUrl: './knx-switches.component.html',
  styles: [`
    :host { display: block; }
    .knx-kind-light  { --knx-accent-rgb: var(--bs-warning-rgb); }
    .knx-kind-socket { --knx-accent-rgb: var(--bs-success-rgb); }
    .knx-tile-label { font-size: .68rem; letter-spacing: .04em; text-transform: uppercase; color: var(--bs-secondary-color); line-height: 1.2; }
    .knx-tile-value { font-size: 1.35rem; font-weight: 600; line-height: 1.15; font-variant-numeric: tabular-nums; }
    .knx-tile-sub   { font-size: .72rem; color: var(--bs-secondary-color); line-height: 1.25; }
    .knx-swatch     { display: inline-block; width: .7rem; height: .7rem; border-radius: 50%; margin-right: .3rem; vertical-align: -.08rem;
                      background: rgb(var(--knx-accent-rgb)); }
    .knx-swatch-off { background: transparent; box-shadow: inset 0 0 0 1px var(--bs-secondary-color); }
    .knx-floor      { font-size: .65rem; letter-spacing: .06em; }
    .knx-room       { transition: box-shadow .2s; }
    .knx-room-on    { box-shadow: inset 3px 0 0 rgb(var(--knx-accent-rgb)); }
    .knx-room-title { font-size: .8rem; }
    .knx-room-count { font-size: .68rem; font-variant-numeric: tabular-nums; color: var(--bs-secondary-color); white-space: nowrap; }
    .knx-room-count b { color: var(--bs-body-color); font-weight: 600; }
    .knx-row        { display: flex; align-items: center; gap: .5rem; padding: .28rem 0; min-height: 2rem; }
    .knx-row + .knx-row { border-top: 1px solid var(--bs-border-color-translucent); }
    .knx-name       { font-size: .8rem; line-height: 1.2; }
    .knx-since      { font-size: .64rem; line-height: 1.2; color: var(--bs-secondary-color); }
    .knx-dim        { flex-shrink: 0; width: 2.7rem; text-align: right; font-size: .68rem; line-height: 1.1;
                      font-variant-numeric: tabular-nums; color: var(--bs-secondary-color); }
    .knx-dim-bar    { display: block; height: 3px; margin-top: 2px; border-radius: 2px; overflow: hidden; background: var(--bs-secondary-bg); }
    .knx-dim-bar > span { display: block; height: 100%; border-radius: 2px; background: rgb(var(--bs-warning-rgb)); }
    .knx-pill       { flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; gap: .2rem;
                      min-width: 3.3rem; padding: .14rem .45rem; border-radius: 999px; border: 1px solid transparent;
                      font-size: .66rem; font-weight: 700; letter-spacing: .05em; line-height: 1.2; }
    .knx-pill svg   { width: .72rem; height: .72rem; flex-shrink: 0; }
    .knx-pill-off   { color: var(--bs-secondary-color); border-color: var(--bs-border-color); background: transparent; font-weight: 600; }
    .knx-pill-unknown { color: var(--bs-secondary-color); border: 1px dashed var(--bs-border-color); background: transparent; }
    .knx-glow       { box-shadow: 0 0 .55rem rgba(var(--bs-warning-rgb), .55); }
    .knx-hint       { font-size: .65rem; }
    /* Room rows of the mobile view (styles.css pattern (d)): accent bar while a device is on | room name | device chips */
    /* the name as wide as it needs (it wraps beyond 55 %), the chips take the rest */
    .knx-lrow       { grid-template-columns: auto minmax(0, max-content) minmax(45%, 1fr); }
    .knx-lrow-state { display: block; align-self: stretch; width: 4px; min-height: 28px; border-radius: 2px; background: var(--bs-secondary-bg); }
    .knx-lrow-state.on { background: rgb(var(--knx-accent-rgb)); }
    .knx-lrow-name  { min-width: 0; font-size: .875rem; font-weight: 600; line-height: 1.25; overflow-wrap: break-word; }
    .knx-chips      { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: .3rem; min-width: 0; }
    .knx-chip       { display: inline-flex; align-items: center; gap: .3rem; min-width: 0; max-width: 100%;
                      padding: .2rem .55rem; border-radius: 999px; border: 1px solid transparent;
                      font-size: .75rem; font-weight: 600; line-height: 1.25; }
    .knx-chip.knx-pill-off     { font-weight: 400; color: var(--bs-secondary-color); border-color: var(--bs-border-color); }
    .knx-chip.knx-pill-unknown { font-weight: 400; color: var(--bs-secondary-color); border: 1px dashed var(--bs-border-color); }
    .knx-chip svg   { width: .8rem; height: .8rem; flex-shrink: 0; }
    .knx-chip-extra { font-weight: 400; opacity: .85; white-space: nowrap; }
    @media (min-width: 576px) { .knx-tile-value { font-size: 1.6rem; } }
    /* Mobile view (< 768 px, landscape phones included): no text under 11 px */
    @media (max-width: 767.98px) {
      .knx-tile-label, .knx-hint { font-size: .6875rem; }
    }
    /* Mobile view from 576 px: the filter stays at the end of its row, in the phones' touch size (styles.css
       sizes .dash-seg only below 576 px) */
    @media (min-width: 576px) and (max-width: 767.98px) {
      .dash-seg > .btn { min-height: var(--dash-touch-sm); padding: 4px 12px; font-size: .8125rem; }
      .dash-seg > .btn-outline-secondary { --bs-btn-color: var(--bs-secondary-color); }
      /* "Zuletzt geschaltet": device and room wrap like on phones (styles.css .m-wrap acts below 576 px only) */
      .knx-tile-sub.m-wrap { white-space: normal; overflow: visible; text-overflow: clip; }
    }
  `],
})
export class KnxSwitchesComponent implements OnChanges, OnDestroy {
  @Input() kind: KnxSwitchKind = 'light';
  /** Devices of this kind in backend location order; null while no snapshot arrived yet (skeletons). */
  @Input() devices:   KnxDeviceState[] | null = null;
  @Input() updatedAt: number | null = null;
  @Input() flash      = false;
  @Input() liveDotOn  = true;
  @Input() nowTick    = Date.now();
  @Input() darkMode   = false;

  cfg = KINDS.light;
  onlyOn = false;
  summary: SwitchSummary | null = null;
  /** All rows / switched-on rows only (both prebuilt, the filter just swaps them) and the one shown. */
  private floorsAll: SwitchFloor[] = [];
  private floorsOn:  SwitchFloor[] = [];
  floors: SwitchFloor[] = [];

  /** Skeleton grid while loading (stable reference). */
  readonly skeletonTiles = [0, 1, 2, 3];
  readonly skeletonRooms = [0, 1, 2];
  readonly skeletonCards = [0, 1, 2, 3, 4, 5];
  /** Mobile view (< 768 px): one list row per room with device chips instead of the room cards. */
  readonly listView = new PhoneMedia(undefined, MOBILE_VIEW_QUERY);

  /** UI language: argument of the pure `knxName` pipe (English room / floor / device names unless German). */
  lang: string;
  private langSub: Subscription;
  private linkSub: Subscription;

  constructor(readonly translate: TranslateService, private readonly link: DeepLinkService) {
    this.lang = translate.currentLang || translate.defaultLang || 'en';
    // "seit …" uses locale date formats and "last switched" a translated location: rebuild on a language switch.
    this.langSub = this.translate.onLangChange.subscribe((e: LangChangeEvent) => {
      this.lang = e.lang;
      this.build();
    });
    // Back / forward or an edited link: its filter (else the saved one, then named in the link)
    this.linkSub = link.external$.subscribe(() => {
      this.onlyOn = this.linkedOnlyOn();
      this.floors = this.onlyOn ? this.floorsOn : this.floorsAll;
      this.linkFilter();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['kind']) {
      this.cfg = KINDS[this.kind] ?? KINDS.light;
      this.onlyOn = this.linkedOnlyOn();
      this.linkFilter();
    }
    // nowTick / liveDotOn tick every second: only rebuild on a new snapshot.
    if (changes['devices'] || changes['kind']) this.build();
  }

  ngOnDestroy(): void {
    this.langSub.unsubscribe();
    this.linkSub.unsubscribe();
    this.listView.destroy();
  }

  /** Filter button: shown, saved (this browser's default) and in the deep link with its sub-tab
   *  ("#knx/lights?knx.lights.onlyOn=1" / "=0"). The sub-tab may be shown from the saved one without a
   *  path ("#knx"): a click is a user action on it, so it is named — else a shared link would open the
   *  recipient's own sub-tab. "Alle" is written as "0" too: a missing key means the recipient's saved filter. */
  setOnlyOn(v: boolean): void {
    this.onlyOn = v;
    this.floors = v ? this.floorsOn : this.floorsAll;
    saveFlag(this.cfg.flagKey, v);
    if (!this.link.isOpen(this.cfg.view)) this.link.openView(this.cfg.view);
    this.link.set(this.cfg.linkKey, v ? '1' : '0');
  }

  /** While the link names this sub-tab, it always carries the filter shown ("1" / "0"): a link without the
   *  key would show the recipient's saved filter. Without the sub-tab in the link (the saved sub-tab, e.g.
   *  "#knx" or another tab on desktop), the URL stays as it is. */
  private linkFilter(): void {
    if (this.link.isOpen(this.cfg.view) && this.link.get(this.cfg.linkKey) == null) {
      this.link.set(this.cfg.linkKey, this.onlyOn ? '1' : '0');
    }
  }

  /** "Nur an" from the deep link ("1" / "0") — it wins, without changing the saved flag (only a click
   *  does) — else the flag saved in this browser. */
  private linkedOnlyOn(): boolean {
    const v = this.link.get(this.cfg.linkKey);
    return v === '1' ? true : v === '0' ? false : loadFlag(this.cfg.flagKey);
  }

  updatedAgo(ts: number | null): string { return fmtAgo(ts, this.nowTick); }

  trackFloor = (_: number, f: SwitchFloor) => f.key;
  trackRoom  = (_: number, r: SwitchRoom) => r.key;
  trackRow   = (_: number, d: SwitchRow) => d.id;

  private build(): void {
    const devices = this.devices;
    if (!devices) {
      this.summary = null;
      this.floors = this.floorsAll = this.floorsOn = [];
      return;
    }
    const fmt = new KnxSinceFormatter();
    const rows = devices.map(d => this.row(d, fmt));

    this.floorsAll = groupByLocation(rows).map(f => ({
      key: f.key,
      floor: f.floor,
      rooms: f.rooms.map(r => ({ ...r, on: r.items.filter(d => d.state === 'on').length, total: r.items.length })),
    }));
    this.floorsOn = this.floorsAll
      .map(f => ({ ...f, rooms: f.rooms.filter(r => r.on > 0).map(r => ({ ...r, items: r.items.filter(d => d.state === 'on') })) }))
      .filter(f => f.rooms.length > 0);
    this.floors = this.onlyOn ? this.floorsOn : this.floorsAll;

    let on = 0, off = 0, dimmers = 0, dimmersOn = 0, dimSum = 0, dimN = 0;
    let last: SwitchRow | null = null;
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (r.state === 'on') on++;
      else if (r.state === 'off') off++;
      if (devices[i].dimmable) {
        dimmers++;
        if (r.state === 'on') {
          dimmersOn++;
          if (r.brightness != null) { dimSum += r.brightness; dimN++; }
        }
      }
      if (r.sinceTs != null && (last === null || r.sinceTs > (last.sinceTs ?? 0))) last = r;
    }
    // The trailing "Ohne Raum" group (floor null) is not a room: keep it out of the room tile.
    const placedRooms = (fs: SwitchFloor[]) => fs.reduce((n, f) => f.floor !== null ? n + f.rooms.length : n, 0);
    this.summary = {
      total: rows.length, on, off, dimmers, dimmersOn,
      rooms:   placedRooms(this.floorsAll),
      roomsOn: placedRooms(this.floorsOn),
      avgDimPct: dimN ? Math.round(dimSum / dimN) : null,
      last,
      lastWhere: last ? knxLocationName(last.floor, last.room, this.lang) : '',
    };
  }

  private row(d: KnxDeviceState, fmt: KnxSinceFormatter): SwitchRow {
    const state: SwitchState = d.on === true ? 'on' : d.on === false ? 'off' : 'unknown';
    const since = typeof d.since === 'number' && d.since > 0 ? d.since : null;
    return {
      id: d.id,
      name: d.name,
      floor: d.floor ?? null,
      room: d.room ?? null,
      state,
      stateKey: state === 'on' ? 'KNX_ON' : state === 'off' ? 'KNX_OFF' : null,
      pillCls: state === 'on' ? this.cfg.onCls : state === 'off' ? 'knx-pill-off' : 'knx-pill-unknown',
      brightness: d.dimmable && state === 'on' ? pct(d.brightness) : null,
      sinceTs: since,
      since: since != null ? fmt.short(since) : null,
      sinceFull: since != null ? fmt.long(since) : null,
    };
  }
}
